import React, { useState, useMemo } from "react";
import {
  ToolShell, Field, UnitNumber, Select, Result,
  hazenWilliamsHeadLoss, pipeVelocity, toBase, fromBase, fmt, UNITS,
  SpecCard, SpecValue, SuffixNum,
} from "./_shared";

/**
 *  NETWORK FRICTION LOSS
 *  --------------------------------------------------------------
 *  Three-tier linear path:  Mainline → Sub-main → Lateral.
 *  Each tier is its own pipe with its own material, ID, length and
 *  flow rate.  Hazen-Williams head loss is computed per tier and
 *  summed for total network loss.  Adds static head + required
 *  outlet pressure to give pump head required.
 *  --------------------------------------------------------------
 */

const MATERIALS = [
  { value: "150",  label: "HDPE / Polyethylene · C = 150" },
  { value: "150b", label: "PVC · C = 150" },
  { value: "140",  label: "Copper · C = 140" },
  { value: "130",  label: "Cast iron (cement-lined) · C = 130" },
  { value: "120",  label: "Galvanised steel · C = 120" },
  { value: "100",  label: "Steel (older / mild scale) · C = 100" },
];

// Real Hazen-Williams roughness coefficients keyed by the "value" above.
// (Two PVC-grade values share C = 150 for the UI; the math reads C from here.)
const C_FROM_VALUE = {
  "150":  150,
  "150b": 150,
  "140":  140,
  "130":  130,
  "120":  120,
  "100":  100,
};

function velocityStatus(v) {
  if (!v) return [undefined, null];
  if (v > 3.0) return ["alert", "Above 3.0 m/s — erosive, water hammer risk."];
  if (v > 2.5) return ["warn",  "Above 2.5 m/s — acceptable for short runs only."];
  if (v < 0.6) return ["warn",  "Below 0.6 m/s — sedimentation risk."];
  return ["ok", null];
}

// Christiansen's F-factor for a lateral with N equally-spaced outlets.
// Hazen-Williams flow exponent m = 1.852:
//   F(N) = 1/(m+1) + 1/(2N) + √(m-1) / (6 N²)
//        = 0.3506 + 0.5/N + 0.1538/N²
// Limit as N → ∞ is 1/(m+1) ≈ 0.3506.
// Multiplying the constant-flow Hazen-Williams head loss by F gives the
// real friction along a lateral that discharges flow along its length.
function christiansenF(outlets) {
  const n = Math.max(0, Math.floor(outlets || 0));
  if (n <= 1) return 1.0;
  return 0.3506 + 0.5 / n + 0.1538 / (n * n);
}

// Pure compute for a single segment — no side effects, no mutation.
function computeSegment(state) {
  const id_m   = toBase("length_small", state.idVal, state.idUnit) / 1000;
  const len_m  = toBase("length_big",   state.lenVal, state.lenUnit);
  const Q_m3s  = toBase("flow",         state.flowVal, state.flowUnit);
  const C      = C_FROM_VALUE[state.material] ?? parseFloat(state.material);
  const hL_raw = hazenWilliamsHeadLoss({ flow_m3s: Q_m3s, id_m, length_m: len_m, C });
  // Christiansen correction — only meaningful when the segment is a lateral
  // with emitters along its length. Default outlets = 1 → F = 1 → no change.
  const F = christiansenF(state.outlets);
  const hL_m = hL_raw == null ? null : hL_raw * F;
  const v_mps  = pipeVelocity({ flow_m3s: Q_m3s, id_m });
  return { id_m, len_m, Q_m3s, C, hL_raw, hL_m, F, v_mps };
}

function MiniStat({ label, value, unit, status, note }) {
  const ring = status === "alert" ? "#B85C38" : status === "warn" ? "#C2922E" : "rgba(12,42,64,0.10)";
  return (
    <div style={{ background: "#FCFCFA", border: `1px solid ${ring}`, padding: "10px 12px" }}>
      <div className="font-mono uppercase"
        style={{ fontSize: "0.55rem", letterSpacing: "0.2em", color: "rgba(12,42,64,0.55)" }}>
        {label}
      </div>
      <div className="flex items-baseline gap-1.5 mt-1">
        <span style={{ fontSize: "1.05rem", fontWeight: 600, color: "#0C2A40" }}>{value}</span>
        <span className="font-mono" style={{ fontSize: "0.68rem", color: "rgba(12,42,64,0.6)" }}>{unit}</span>
      </div>
      {note && (
        <div style={{ fontSize: "0.7rem", color: status === "alert" ? "#B85C38" : "#C2922E", marginTop: 4, lineHeight: 1.3 }}>
          {note}
        </div>
      )}
    </div>
  );
}

function Segment({ label, accent, state, setState, computed, testidPrefix, showOutlets = false }) {
  const { hL_raw, hL_m, F, v_mps } = computed;
  const [vStat, vNote] = velocityStatus(v_mps);
  const fApplied = showOutlets && (state.outlets || 0) > 1 && F < 1;

  return (
    <div
      className="p-5 md:p-6"
      style={{
        background: "#FFFFFF",
        border: "1px solid rgba(12,42,64,0.08)",
        borderLeft: `3px solid ${accent}`,
      }}
      data-testid={`${testidPrefix}-card`}
    >
      <div
        className="font-mono uppercase mb-4"
        style={{ fontSize: "0.62rem", letterSpacing: "0.22em", color: accent, fontWeight: 700 }}
      >
        {label}
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Field label="Pipe material">
          <Select value={state.material} onChange={(v) => setState({ ...state, material: v })}
            options={MATERIALS} testid={`${testidPrefix}-material`} />
        </Field>
        <Field label="Internal Ø">
          <UnitNumber dim="length_small" value={state.idVal}
            onChange={(v) => setState({ ...state, idVal: v })}
            unit={state.idUnit} onUnitChange={(u) => setState({ ...state, idUnit: u })}
            units={["mm", "cm", "m", "inch"]} testid={`${testidPrefix}-id`} />
        </Field>
        <Field label="Length">
          <UnitNumber dim="length_big" value={state.lenVal}
            onChange={(v) => setState({ ...state, lenVal: v })}
            unit={state.lenUnit} onUnitChange={(u) => setState({ ...state, lenUnit: u })}
            units={["m", "ft", "km"]} testid={`${testidPrefix}-len`} />
        </Field>
        <Field label="Flow rate">
          <UnitNumber dim="flow" value={state.flowVal}
            onChange={(v) => setState({ ...state, flowVal: v })}
            unit={state.flowUnit} onUnitChange={(u) => setState({ ...state, flowUnit: u })}
            units={["Lps", "Lpm", "m3h", "gpm"]} testid={`${testidPrefix}-flow`} />
        </Field>
      </div>

      {showOutlets && (
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <Field label="Outlets along this lateral"
            hint="Number of equally-spaced emitters / sprinklers / drippers along the run. Leave at 1 to treat the lateral as a single end discharge (no distributed outflow).">
            <div className="flex items-stretch">
              <input
                type="number"
                value={state.outlets || 1}
                step="1"
                min="1"
                onChange={(e) => setState({ ...state, outlets: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                className="flex-1 min-w-0 px-3 py-2.5 bg-white border outline-none focus:border-[#1FB6C1]"
                style={{ borderColor: "rgba(12,42,64,0.16)", color: "#0C2A40", fontSize: "0.95rem", fontWeight: 500 }}
                data-testid={`${testidPrefix}-outlets`}
              />
              <span className="inline-flex items-center px-3 border border-l-0 font-mono"
                style={{ background: "#FAFAF7", borderColor: "rgba(12,42,64,0.16)", color: "rgba(12,42,64,0.65)", fontSize: "0.72rem" }}>
                emitters
              </span>
            </div>
          </Field>
          {fApplied && (
            <div className="flex items-end">
              <div className="font-light leading-snug"
                style={{ fontSize: "0.74rem", color: "rgba(12,42,64,0.7)" }}
                data-testid={`${testidPrefix}-f-note`}
              >
                Christiansen F-factor for {state.outlets} outlets ={" "}
                <span className="font-mono" style={{ color: "#0C2A40", fontWeight: 600 }}>{fmt(F, 3)}</span>{" "}
                — friction reduced from{" "}
                <span className="font-mono">{fmt(hL_raw, 2)} m</span> (constant flow) to{" "}
                <span className="font-mono" style={{ color: "#0C2A40", fontWeight: 600 }}>{fmt(hL_m, 2)} m</span> (distributed outflow).
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-3 mt-4">
        <MiniStat label="Head loss" value={fmt(hL_m)} unit="m H₂O" />
        <MiniStat label="Pressure drop" value={fmt(hL_m !== null ? hL_m * 0.0980665 : null)} unit="bar" />
        <MiniStat label="Velocity" value={fmt(v_mps)} unit="m/s" status={vStat} note={vNote} />
      </div>
    </div>
  );
}

export default function NetworkFrictionCalculator() {
  // Defaults sized for a typical lodge / smallholding irrigation network.
  const [main, setMain] = useState({
    material: "150", idVal: 75, idUnit: "mm", lenVal: 80, lenUnit: "m",
    flowVal: 5, flowUnit: "Lps",
  });
  const [submain, setSubmain] = useState({
    material: "150", idVal: 50, idUnit: "mm", lenVal: 40, lenUnit: "m",
    flowVal: 2.5, flowUnit: "Lps",
  });
  const [lateral, setLateral] = useState({
    material: "150", idVal: 25, idUnit: "mm", lenVal: 30, lenUnit: "m",
    flowVal: 0.8, flowUnit: "Lps",
    outlets: 1,           // 1 = single end discharge; > 1 enables Christiansen F-factor
  });

  // System pressure components
  const [staticVal, setStaticVal] = useState(8);     // elevation difference, m
  const [staticUnit, setStaticUnit] = useState("m");
  const [outletPressureBar, setOutletPressureBar] = useState(2.0);  // emitter / sprinkler design pressure
  const [safetyPct, setSafetyPct] = useState(10);    // pump safety margin

  // Pure computations — no render-time mutation.
  const mainCalc    = useMemo(() => computeSegment(main),    [main]);
  const submainCalc = useMemo(() => computeSegment(submain), [submain]);
  const lateralCalc = useMemo(() => computeSegment(lateral), [lateral]);

  const totalLoss_m = (mainCalc.hL_m ?? 0) + (submainCalc.hL_m ?? 0) + (lateralCalc.hL_m ?? 0);

  const static_m       = toBase("length_big", staticVal, staticUnit);
  const outletHead_m   = outletPressureBar / 0.0980665;     // bar → m of head
  const totalHead_m_raw = totalLoss_m + static_m + outletHead_m;
  const totalHead_m    = totalHead_m_raw * (1 + safetyPct / 100);

  return (
    <ToolShell
      slug="network-friction"
      title="Network friction loss"
      oneLiner="Three-tier hydraulic path — mainline + sub-main + lateral — summed to a total head requirement at the pump. Useful for sizing irrigation, manifold and pressurised-distribution pumps."
      onExport={() => ({
        inputs: [
          { label: "── Mainline ──", value: "" },
          { label: "Material", value: MATERIALS.find((m) => m.value === main.material)?.label ?? main.material },
          { label: "Internal Ø", value: main.idVal, unit: main.idUnit },
          { label: "Length", value: main.lenVal, unit: main.lenUnit },
          { label: "Flow", value: main.flowVal, unit: UNITS.flow.labels[main.flowUnit] },
          { label: "── Sub-main ──", value: "" },
          { label: "Material", value: MATERIALS.find((m) => m.value === submain.material)?.label ?? submain.material },
          { label: "Internal Ø", value: submain.idVal, unit: submain.idUnit },
          { label: "Length", value: submain.lenVal, unit: submain.lenUnit },
          { label: "Flow", value: submain.flowVal, unit: UNITS.flow.labels[submain.flowUnit] },
          { label: "── Lateral ──", value: "" },
          { label: "Material", value: MATERIALS.find((m) => m.value === lateral.material)?.label ?? lateral.material },
          { label: "Internal Ø", value: lateral.idVal, unit: lateral.idUnit },
          { label: "Length", value: lateral.lenVal, unit: lateral.lenUnit },
          { label: "Flow", value: lateral.flowVal, unit: UNITS.flow.labels[lateral.flowUnit] },
          { label: "Outlets along the lateral", value: lateral.outlets || 1, unit: "emitters" },
          ...((lateral.outlets || 1) > 1 ? [
            { label: "Christiansen F-factor (HW, m=1.852)", value: fmt(lateralCalc.F, 3), unit: "× friction" },
          ] : []),
          { label: "── System ──", value: "" },
          { label: "Static elevation difference", value: staticVal, unit: staticUnit },
          { label: "Required outlet pressure", value: outletPressureBar, unit: "bar" },
          { label: "Pump safety margin", value: safetyPct, unit: "%" },
        ],
        results: [
          { label: "Mainline head loss",   value: fmt(mainCalc.hL_m),    unit: "m H₂O" },
          { label: "Sub-main head loss",   value: fmt(submainCalc.hL_m), unit: "m H₂O" },
          { label: "Lateral head loss",    value: fmt(lateralCalc.hL_m), unit: "m H₂O" },
          { label: "Total friction loss",  value: fmt(totalLoss_m), unit: "m H₂O",
            alts: [{ value: fmt(totalLoss_m * 0.0980665), unit: "bar" }] },
          { label: "Static head",          value: fmt(static_m), unit: "m" },
          { label: "Outlet pressure (head equivalent)", value: fmt(outletHead_m), unit: "m" },
          { label: "Total dynamic head (pre-margin)",   value: fmt(totalHead_m_raw), unit: "m" },
          { label: "Pump head required (with margin)", value: fmt(totalHead_m), unit: "m",
            alts: [{ value: fmt(totalHead_m * 0.0980665), unit: "bar" }] },
        ],
        notes: [
          "Per-segment Hazen-Williams hL = 10.67 × L × Q^1.852 / (C^1.852 × D^4.87).",
          "Lateral friction is corrected by Christiansen's F-factor when outlets > 1 (F = 1/(m+1) + 1/(2N) + √(m−1)/(6N²) with m = 1.852).",
          "Pump TDH = Σ friction loss + static head + outlet pressure head × (1 + safety margin).",
          "Single critical path only — multi-branch networks need a per-segment graph analysis.",
        ],
        spec: {
          title: "Network pump head specification",
          body: `Mainline + sub-main + lateral friction = <strong>${fmt(totalLoss_m, 2)} m</strong> at the critical path. Static head <strong>${fmt(static_m, 1)} m</strong> + outlet pressure <strong>${outletPressureBar} bar</strong> (${fmt(outletHead_m, 1)} m) + <strong>${safetyPct} %</strong> safety margin → pump TDH = <strong>${fmt(totalHead_m, 1)} m</strong> (<strong>${fmt(totalHead_m * 0.0980665, 2)} bar</strong>) at <strong>${main.flowVal} ${UNITS.flow.labels[main.flowUnit]}</strong>.`,
        },
      })}
    >
      <div
        className="mb-5 p-4"
        style={{ background: "#FFFBF2", border: "1px solid rgba(194,146,46,0.30)" }}
        data-testid="nfl-scope-banner"
      >
        <div className="font-mono uppercase mb-1.5"
          style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "#8A6914" }}>
          Scope of this tool
        </div>
        <p className="font-light leading-relaxed" style={{ fontSize: "0.82rem", color: "#0C2A40" }}>
          Models <strong>one critical path</strong> from pump to a single emitter run:
          one mainline → one sub-main → one lateral. For networks with multiple
          parallel branches at any tier, run the tool against your{" "}
          <strong>worst-case path</strong> (longest run, highest elevation, smallest pipe)
          to size the pump conservatively. Multi-branch network design — pressure
          balancing across zones, simultaneous-zone peak flow, per-zone critical
          pressure — is the engineering work the HidroVerse team handles directly.
        </p>
      </div>

      <div className="space-y-4">
        <Segment label="Mainline · Source → manifold"      accent="#0C2A40" state={main}    setState={setMain}    computed={mainCalc}    testidPrefix="nfl-main" />
        <Segment label="Sub-main · Manifold → zone valve"  accent="#1FB6C1" state={submain} setState={setSubmain} computed={submainCalc} testidPrefix="nfl-sub" />
        <Segment label="Lateral · Zone valve → emitters"   accent="#3A8FB0" state={lateral} setState={setLateral} computed={lateralCalc} testidPrefix="nfl-lat" showOutlets />
      </div>

      <div className="mt-6 p-5 md:p-6"
        style={{ background: "#FCFCFA", border: "1px solid rgba(12,42,64,0.08)" }}>
        <div className="font-mono uppercase mb-4"
          style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.6)" }}>
          System pressure components
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          <Field label="Static elevation difference" hint="Vertical lift from pump centreline to highest emitter.">
            <UnitNumber dim="length_big" value={staticVal} onChange={setStaticVal}
              unit={staticUnit} onUnitChange={setStaticUnit} units={["m", "ft"]} testid="nfl-static" />
          </Field>
          <Field label="Required outlet pressure" hint="Operating pressure of the emitter / sprinkler at the outlet.">
            <SuffixNum value={outletPressureBar} onChange={setOutletPressureBar} suffix="bar" testid="nfl-outlet" />
          </Field>
          <Field label="Pump safety margin" hint="Typical 10 – 15 % over the calculated TDH.">
            <SuffixNum value={safetyPct} onChange={setSafetyPct} suffix="%" testid="nfl-safety" />
          </Field>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
        <Result label="Total friction loss"
          primary={{ value: fmt(totalLoss_m), unit: "m H₂O" }}
          alts={[{ value: fmt(totalLoss_m * 0.0980665), unit: "bar" }]}
          note="Sum of all three tiers."
          testid="nfl-result-loss" />
        <Result label="Static head"
          primary={{ value: fmt(static_m), unit: "m" }}
          alts={[{ value: fmt(fromBase("length_big", static_m, "ft")), unit: "ft" }]}
          testid="nfl-result-static" />
        <Result label="Outlet pressure (head)"
          primary={{ value: fmt(outletHead_m), unit: "m" }}
          alts={[{ value: fmt(outletPressureBar), unit: "bar" }]}
          testid="nfl-result-outlet" />
        <Result label="Pump head required (TDH)"
          primary={{ value: fmt(totalHead_m), unit: "m" }}
          alts={[
            { value: fmt(totalHead_m_raw), unit: "m pre-margin" },
            { value: fmt(totalHead_m * 0.0980665), unit: "bar" },
          ]}
          note={`Includes ${safetyPct} % safety margin. Spec the pump curve at this head.`}
          testid="nfl-result-tdh" />
      </div>

      <SpecCard
        eyebrow="Network pump head specification"
        testid="nfl-spec-card"
        footer="Single critical path only — multi-branch networks need a per-segment graph analysis."
      >
        Mainline + sub-main + lateral friction ={" "}
        <SpecValue>{fmt(totalLoss_m, 2)} m</SpecValue> at the critical path. Static head{" "}
        <SpecValue>{fmt(static_m, 1)} m</SpecValue> + outlet pressure{" "}
        <SpecValue>{outletPressureBar} bar</SpecValue> ({fmt(outletHead_m, 1)} m) +{" "}
        <SpecValue>{safetyPct} %</SpecValue> safety margin → pump TDH ={" "}
        <SpecValue>{fmt(totalHead_m, 1)} m</SpecValue> ({fmt(totalHead_m * 0.0980665, 2)} bar) at{" "}
        <SpecValue>{main.flowVal} {UNITS.flow.labels[main.flowUnit]}</SpecValue>.
      </SpecCard>
    </ToolShell>
  );
}
